import type { Position } from '@/types'

/**
 * Trade-like input for position calculations.
 * Uses the shape from TradePage's local Trade interface.
 */
export interface TradeLike {
  id: string
  ticker: string
  action: string
  strikePrice: number
  premium: number
  qty: number
  commission: number
  fees: number
  total: number
}

export interface AssignmentResult {
  closeTotal: number
  netPosition: number
  costBasis: number
  sharesAcquired: number
}

export interface SoldResult {
  shareAppreciation: number
  netPosition: number
}

/**
 * Calculate the result of an assignment close action.
 * Assignment means shares are acquired at the strike price.
 * The premium collected is the realized profit — capital outlay is NOT a loss.
 */
export function calculateAssignmentResult(trade: TradeLike): AssignmentResult {
  return {
    closeTotal: 0,
    netPosition: trade.total,
    costBasis: trade.strikePrice,
    sharesAcquired: trade.qty * 100,
  }
}

/**
 * Create a Position record from a CSP trade that was assigned.
 */
export function createPositionFromAssignment(trade: TradeLike, closeDate: string): Omit<Position, 'id'> {
  return {
    accountSlug: '',  // caller must set this
    ticker: trade.ticker,
    quantity: trade.qty * 100,
    costBasis: trade.strikePrice,
    acquisitionDate: closeDate,
    sourceTradeId: trade.id,
    status: 'open',
  }
}

/**
 * Merge two positions for the same ticker using weighted average cost basis.
 */
export function mergePositions(existing: Position, newPosition: Omit<Position, 'id'>): Position {
  const totalQty = existing.quantity + newPosition.quantity
  const weightedCostBasis =
    (existing.quantity * existing.costBasis + newPosition.quantity * newPosition.costBasis) / totalQty

  return {
    ...existing,
    quantity: totalQty,
    costBasis: Math.round(weightedCostBasis * 100) / 100,
  }
}

/**
 * Reduce a position's quantity when shares are sold.
 * If all shares are sold, mark the position as closed.
 */
export function reducePosition(position: Position, sharesSold: number, closeDate?: string): Position {
  const newQty = position.quantity - sharesSold
  return {
    ...position,
    quantity: newQty,
    status: newQty <= 0 ? 'closed' : 'open',
    closedDate: newQty <= 0 ? (closeDate || new Date().toISOString().split('T')[0]) : undefined,
  }
}

/**
 * Calculate unrealized P&L for an open position.
 */
export function calculateUnrealizedPnL(position: Position, currentPrice: number): number {
  return (currentPrice - position.costBasis) * position.quantity
}

/**
 * Calculate the result of selling shares (Sold close action).
 * Share appreciation = (soldPrice - costBasis) × sharesSold
 * Net position = CC premium (trade.total) + share appreciation
 */
export function calculateSoldResult(
  trade: TradeLike,
  soldPrice: number,
  sharesSold: number,
  costBasis: number
): SoldResult {
  const shareAppreciation = (soldPrice - costBasis) * sharesSold
  const netPosition = trade.total + shareAppreciation
  return {
    shareAppreciation: Math.round(shareAppreciation * 100) / 100,
    netPosition: Math.round(netPosition * 100) / 100,
  }
}
